import React from 'react';

import {
  ConfirmationDialog,
  ConfirmationDialogVariant,
} from 'src/components/generic/ConfirmationDialog';

import type { LockAction } from './lock-helpers';

export type { LockAction };
export type LockTarget = 'cell' | 'column' | 'rows';

interface LockConfirmationModalBaseProps {
  action: LockAction;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
  open: boolean;
}

/**
 * `rows` target surfaces a cell count in the copy. Select-all over a
 * virtualized grid cannot produce an accurate count (only materialized rows
 * are enumerable client-side), so the `'all'` scope phrases the message
 * without a number while `'selection'` carries the materialized count.
 */
export type LockConfirmationModalProps =
  | (LockConfirmationModalBaseProps & { target: 'cell' })
  | (LockConfirmationModalBaseProps & { target: 'column' })
  | (LockConfirmationModalBaseProps & { count: number; scope: 'selection'; target: 'rows' })
  | (LockConfirmationModalBaseProps & { scope: 'all'; target: 'rows' });

const ACTION_COPY = {
  lock: {
    description: 'The answers in locked cells remain fixed between AI runs.',
    variant: ConfirmationDialogVariant.Warn,
    verb: 'Lock',
  },
  unlock: {
    description: 'The answers in unlocked cells can be overwritten by AI.',
    variant: ConfirmationDialogVariant.Warn,
    verb: 'Unlock',
  },
} satisfies Record<
  LockAction,
  { description: string; variant: ConfirmationDialogVariant; verb: string }
>;

const TITLE_SUFFIX = {
  cell: 'this cell?',
  column: 'all cells in this column?',
  rows: 'all cells in selected rows?',
} satisfies Record<LockTarget, string>;

export function LockConfirmationModal(props: LockConfirmationModalProps): JSX.Element {
  const { action, onCancel, onConfirm, open, target } = props;
  const { description, variant, verb } = ACTION_COPY[action];
  const title = `${verb} ${TITLE_SUFFIX[target]}`;
  const message = buildMessage(props, { description });

  return (
    <ConfirmationDialog
      message={message}
      open={open}
      primaryText={verb}
      secondaryText="Cancel"
      title={title}
      variant={variant}
      onClickPrimary={onConfirm}
      onClickSecondary={onCancel}
      onClose={onCancel}
    />
  );
}

function buildMessage(props: LockConfirmationModalProps, copy: { description: string }): string {
  const { description } = copy;
  const lockText = props.action === 'lock' ? 'locked' : 'unlocked';

  switch (props.target) {
    case 'cell':
      return `This cell will be ${lockText}. ${description}`;
    case 'column':
      return `All cells in this column will be ${lockText}. ${description}`;
    case 'rows': {
      if (props.scope === 'all') {
        return `All cells in every row of this report will be ${lockText}. ${description}`;
      }
      const cellText = props.count === 1 ? 'cell' : 'cells';
      return `${props.count} ${cellText} will be ${lockText}. ${description}`;
    }
    default: {
      const _exhaustive: never = props;
      return _exhaustive;
    }
  }
}
